/**
 * Generador de recibo de pago para impresora térmica 80mm
 * Incluye: datos del cliente, mascota, venta, servicios y consentimiento informado legal (Colombia)
 */

import jsPDF from 'jspdf';

// Ancho estándar para impresora térmica 80mm
const WIDTH_MM = 80;
const MARGIN = 4;
const CONTENT_WIDTH = WIDTH_MM - MARGIN * 2;
const MAX_CHARS = 22; // chars por línea aprox. a 6pt

/**
 * Texto de consentimiento informado que ampara legalmente en Colombia
 * Basado en Ley 2480 de 2025 (Ley Kiara), Ley 1480 (Consumidor), Ley 1581 (Protección de datos)
 */
const CONSENTIMIENTO_INFORMADO_COLOMBIA = [
  'CONSENTIMIENTO INFORMADO:',
  '',
  'El propietario/tutor responsable declara haber sido informado sobre los procedimientos de grooming, riesgos, beneficios y costos asociados, y autoriza expresamente la realización de los servicios contratados.',
  '',
  'Se reconoce que el prestador cumple con los estándares de la Ley 2480 de 2025 (Ley Kiara) para servicios de cuidado animal.',
  '',
  'Los datos aquí consignados son tratados conforme a la Ley 1581 de 2012 (Protección de datos personales).',
  '',
  'Recibo generado por sistema. Documento válido como comprobante de pago.'
];

/**
 * Divide texto en líneas que caben en el ancho de 80mm
 */
const wrapText = (doc, text, maxWidth = CONTENT_WIDTH - 4) => {
  if (!text) return [];
  return doc.splitTextToSize(String(text), maxWidth);
};

/**
 * Genera el PDF del recibo de pago
 * @param {Object} appointment - Cita con client_name, pet_name, phone, document, etc.
 * @param {Object} saleData - Datos de la venta { id_sale, sale_date, total_amount, payment_method_name }
 * @param {Array} services - Servicios del APPOINTMENT_DETAIL
 * @param {Object} itemsMap - Mapa id_item -> { description, name, price }
 * @param {Object} branding - { companyName } opcional
 */
export const generateAppointmentReceiptPdf = (
  appointment,
  saleData,
  services = [],
  itemsMap = {},
  branding = {}
) => {
  const companyName = branding.companyName || 'DotoniWoof';
  const legalID = branding.legalID || '901.981.483-0';
  const address = branding.address || 'Cra 22 g # 59 a - 31';
  const doc = new jsPDF({
    unit: 'mm',
    format: [WIDTH_MM, 400],
    hotfixes: ['px_scaling']
  });
  doc.setFont('helvetica');

  let y = 6;

  // ---- Header empresa ----
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(companyName, WIDTH_MM / 2, y, { align: 'center' });
  y += 5;

  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.text(`NIT: ${legalID}`, WIDTH_MM / 2, y, { align: 'center' });
  y += 4;
  doc.text(address, WIDTH_MM / 2, y, { align: 'center' });
  y += 4;
  doc.text('RECIBO DE PAGO', WIDTH_MM / 2, y, { align: 'center' });
  y += 6;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 5;

  // ---- Datos de la venta ----
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('VENTA', MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.text(`#${saleData?.id_sale || 'N/A'}`, MARGIN, y);
  y += 4;
  const saleDate = saleData?.sale_date ? new Date(saleData.sale_date) : new Date();
  doc.text(saleDate.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }), MARGIN, y);
  y += 4;
  doc.text(`Pago: ${saleData?.payment_method_name || 'N/A'}`, MARGIN, y);
  y += 6;

  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 5;

  // ---- Datos del cliente ----
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENTE', MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  const clientName = appointment?.client_name || appointment?.clientName || 'N/A';
  wrapText(doc, clientName, CONTENT_WIDTH).forEach((line) => {
    doc.text(line, MARGIN, y);
    y += 3.5;
  });
  const docClient = appointment?.document || appointment?.client_document || '';
  if (docClient) { doc.text(`Doc: ${docClient}`, MARGIN, y); y += 3.5; }
  const phone = appointment?.phone || appointment?.phone_whatsapp || '';
  if (phone) { doc.text(`Tel: ${phone}`, MARGIN, y); y += 3.5; }
  y += 3;

  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 5;

  // ---- Datos de la mascota ----
  doc.setFont('helvetica', 'bold');
  doc.text('MASCOTA', MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  const petName = appointment?.pet_name || 'N/A';
  doc.text(petName, MARGIN, y);
  y += 3.5;
  const breed = appointment?.pet_breed || appointment?.breed || '';
  if (breed) { doc.text(`Raza: ${breed}`, MARGIN, y); y += 3.5; }
  y += 3;

  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 5;

  // ---- Servicios ----
  doc.setFont('helvetica', 'bold');
  doc.text('SERVICIOS', MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6);
  doc.text('Descripcion', MARGIN, y);
  doc.text('Cant', MARGIN + 38, y);
  doc.text('P.U', MARGIN + 48, y);
  doc.text('Total', MARGIN + 62, y);
  y += 4;
  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 3;

  let totalCalculado = 0;
  const svcList = Array.isArray(services) ? services : [];
  svcList.forEach((svc) => {
    const it = itemsMap[svc.id_item] || {};
    const desc = (svc.description || it.description || it.name || 'Servicio').toString().substring(0, 18);
    const qty = svc.quantity || 1;
    const pu = parseFloat(svc.unit_price || it.price || 0);
    const subtotal = qty * pu;
    totalCalculado += subtotal;
    doc.text(desc, MARGIN, y);
    doc.text(String(qty), MARGIN + 38, y);
    doc.text(`$${Math.round(pu).toLocaleString('es-CO')}`, MARGIN + 48, y);
    doc.text(`$${Math.round(subtotal).toLocaleString('es-CO')}`, MARGIN + 62, y);
    y += 4;
  });

  y += 3;
  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const saleTotal = Number.parseFloat(saleData?.total_amount);
  const hasSaleTotal = Number.isFinite(saleTotal);
  const hasServices = svcList.length > 0;
  const totalsMatch = hasSaleTotal && Math.abs(saleTotal - totalCalculado) <= 0.01;
  let totalFinal = totalCalculado;

  if (hasSaleTotal && (!hasServices || totalsMatch)) {
    totalFinal = saleTotal;
  } else if (hasSaleTotal && hasServices && !totalsMatch) {
    // Si hay desajuste entre la venta y el detalle, priorizamos el total de líneas para el recibo.
    console.warn(
      '[receipt] Desajuste entre total de venta y servicios',
      {
        idSale: saleData?.id_sale,
        saleTotal,
        totalCalculado
      }
    );
  }

  doc.text('TOTAL:', MARGIN, y);
  doc.text(`$${Math.round(parseFloat(totalFinal)).toLocaleString('es-CO')}`, MARGIN + 50, y, { align: 'right' });
  y += 8;

  doc.line(MARGIN, y, WIDTH_MM - MARGIN, y);
  y += 5;

  // ---- Consentimiento informado ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  CONSENTIMIENTO_INFORMADO_COLOMBIA.forEach((paragraph) => {
    if (paragraph === '') {
      y += 2;
      return;
    }
    const lines = wrapText(doc, paragraph, CONTENT_WIDTH);
    lines.forEach((line) => {
      if (y > 380) {
        doc.addPage([WIDTH_MM, 400]);
        y = 10;
      }
      doc.setFont('helvetica', line.startsWith('CONSENTIMIENTO') ? 'bold' : 'normal');
      doc.text(line, MARGIN, y);
      y += 3;
    });
    y += 1;
  });

  doc.setFont('helvetica', 'normal');
  y += 5;
  doc.setFontSize(5);
  doc.text(`${companyName} - Recibo generado el ${new Date().toLocaleString('es-CO')}`, WIDTH_MM / 2, y, { align: 'center' });

  return doc;
};
